import { apiClient } from './api';
import type {
  BoardDisplayState,
  LiveLessonDisplayOverlays,
  ClassroomEvent,
  LiveLessonCompetencyEnvelope,
  LiveLessonSession,
  LiveSessionListItem,
  LiveLessonQuizState,
  StudentLiveLessonQuizState,
  StudentObservation,
  TemporaryNeedHelpState
} from './liveLessonTypes';

type SessionEnvelope = {
  session: LiveLessonSession | null;
  observations: StudentObservation[];
  helpStates: TemporaryNeedHelpState[];
};

export function buildInitialLiveSession(params: {
  teachingUnitId: number;
  className: string;
  theme: string;
  subjectId?: number;
}): LiveLessonSession {
  const now = new Date().toISOString();
  return {
    id: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}`,
    teachingUnitId: params.teachingUnitId,
    timetableEntryId: null,
    status: 'live',
    startedAt: now,
    endedAt: null,
    classContext: { parentType: 'class', label: params.className.trim() || 'Klasse' },
    sitzplanId: null,
    activeCompetencyIds: [],
    suggestedCompetencyIds: [],
    competencySuggestSource: null,
    currentPhaseId: 'einstieg',
    activeScene: 'focus',
    workMode: 'individual',
    displayPin: null,
    teacherStateVersion: 1,
    boardStateVersion: 1,
    theme: params.theme,
    subjectId: params.subjectId ?? null,
    timerRemainingSec: 10 * 60,
    timerRunning: false
  };
}

export async function fetchLiveSessions(): Promise<LiveSessionListItem[]> {
  const response = await apiClient.get('/live-lesson/sessions');
  return response.data?.data?.sessions ?? [];
}

export async function reactivateLiveSession(teachingUnitId: number): Promise<SessionEnvelope> {
  const response = await apiClient.post(`/live-lesson/${teachingUnitId}/reactivate`);
  return response.data?.data || { session: null, observations: [], helpStates: [] };
}

export async function fetchLiveLessonSession(teachingUnitId: number): Promise<SessionEnvelope> {
  const response = await apiClient.get(`/live-lesson/${teachingUnitId}/session`);
  return response.data?.data || { session: null, observations: [], helpStates: [] };
}

export async function saveLiveLessonSession(
  teachingUnitId: number,
  payload: {
    session: LiveLessonSession;
    observations?: StudentObservation[];
    helpStates?: TemporaryNeedHelpState[];
  }
): Promise<SessionEnvelope> {
  const response = await apiClient.put(`/live-lesson/${teachingUnitId}/session`, payload);
  return response.data?.data || { session: null, observations: [], helpStates: [] };
}

export async function appendLiveLessonEvent(
  teachingUnitId: number,
  payload: { event: ClassroomEvent }
): Promise<void> {
  await apiClient.post(`/live-lesson/${teachingUnitId}/events`, payload);
}

export async function updateLiveLessonBoardState(
  teachingUnitId: number,
  payload: { pin: string; boardDisplayState: BoardDisplayState; liveLessonOverlays?: LiveLessonDisplayOverlays }
): Promise<{ version: number }> {
  const response = await apiClient.post(`/live-lesson/${teachingUnitId}/board-state`, payload);
  return response.data?.data || { version: 0 };
}

export async function commitLiveLessonReview(
  teachingUnitId: number,
  payload: { observationIds: string[] }
): Promise<{ committedIds: string[] }> {
  const response = await apiClient.post(`/live-lesson/${teachingUnitId}/review/commit`, payload);
  return response.data?.data || { committedIds: [] };
}

export async function discardLiveLessonReview(
  teachingUnitId: number,
  payload: { observationIds: string[] }
): Promise<{ discardedIds: string[] }> {
  const response = await apiClient.post(`/live-lesson/${teachingUnitId}/review/discard`, payload);
  return response.data?.data || { discardedIds: [] };
}

export async function destroyLiveSession(teachingUnitId: number): Promise<{ teachingUnitId: number }> {
  const response = await apiClient.post(`/live-lesson/${teachingUnitId}/destroy`);
  return response.data?.data || { teachingUnitId };
}

export async function fetchLiveLessonCompetencies(
  teachingUnitId: number,
  params?: { subjectId?: number; theme?: string }
): Promise<LiveLessonCompetencyEnvelope> {
  const response = await apiClient.get(`/live-lesson/${teachingUnitId}/competencies`, {
    params: {
      ...(params?.subjectId != null ? { subjectId: params.subjectId } : {}),
      ...(params?.theme != null ? { theme: params.theme } : {})
    }
  });
  const data = response.data?.data || {};
  return {
    competencies: Array.isArray(data.competencies) ? data.competencies : [],
    curriculumIds: Array.isArray(data.curriculumIds) ? data.curriculumIds : [],
    activeCompetencyIds: Array.isArray(data.activeCompetencyIds) ? data.activeCompetencyIds : [],
    suggestedCompetencyIds: Array.isArray(data.suggestedCompetencyIds) ? data.suggestedCompetencyIds : [],
    suggestSource: data.suggestSource ?? null,
    theme: data.theme,
    subjectId: data.subjectId ?? null
  };
}

export async function suggestLiveLessonCompetencies(
  teachingUnitId: number,
  payload: { subjectId?: number; theme?: string }
): Promise<LiveLessonCompetencyEnvelope & { session?: LiveLessonSession }> {
  const response = await apiClient.post(`/live-lesson/${teachingUnitId}/competencies/suggest`, payload);
  const data = response.data?.data || {};
  return {
    competencies: Array.isArray(data.competencies) ? data.competencies : [],
    curriculumIds: Array.isArray(data.curriculumIds) ? data.curriculumIds : [],
    activeCompetencyIds: Array.isArray(data.activeCompetencyIds) ? data.activeCompetencyIds : [],
    suggestedCompetencyIds: Array.isArray(data.suggestedCompetencyIds) ? data.suggestedCompetencyIds : [],
    suggestSource: data.suggestSource ?? null,
    session: data.session
  };
}

export async function fetchTimetableNowWidget(): Promise<{
  entries: Array<{
    class_name?: string | null;
    subject_name?: string | null;
    timetable_entry_id?: number | null;
    period_no?: number | null;
    starts_at?: string | null;
    ends_at?: string | null;
  }>;
}> {
  const response = await apiClient.get('/timetables/dashboard-widget');
  return response.data?.data || { entries: [] };
}

export async function generateLiveLessonQuiz(
  teachingUnitId: number,
  payload: { topic: string; subjectId?: number }
): Promise<LiveLessonQuizState | null> {
  const response = await apiClient.post(`/live-lesson/${teachingUnitId}/quiz/generate`, payload);
  return response.data?.data || null;
}

export async function startLiveLessonQuiz(teachingUnitId: number): Promise<LiveLessonQuizState | null> {
  const response = await apiClient.post(`/live-lesson/${teachingUnitId}/quiz/start`);
  return response.data?.data || null;
}

export async function fetchLiveLessonQuizState(teachingUnitId: number): Promise<LiveLessonQuizState | null> {
  const response = await apiClient.get(`/live-lesson/${teachingUnitId}/quiz/state`, {
    params: { t: Date.now() }
  });
  return response.data?.data || null;
}

export async function advanceLiveLessonQuiz(teachingUnitId: number): Promise<LiveLessonQuizState | null> {
  const response = await apiClient.post(`/live-lesson/${teachingUnitId}/quiz/advance`);
  return response.data?.data || null;
}

export async function finishLiveLessonQuiz(teachingUnitId: number): Promise<LiveLessonQuizState | null> {
  const response = await apiClient.post(`/live-lesson/${teachingUnitId}/quiz/finish`);
  return response.data?.data || null;
}

export async function approveLiveLessonQuizParticipant(
  teachingUnitId: number,
  studentId: string
): Promise<LiveLessonQuizState | null> {
  const response = await apiClient.post(`/live-lesson/${teachingUnitId}/quiz/participants/${encodeURIComponent(studentId)}/approve`);
  return response.data?.data || null;
}

export async function updateLiveLessonQuizSettings(
  teachingUnitId: number,
  payload: { allowExternalJoin: boolean }
): Promise<LiveLessonQuizState | null> {
  const response = await apiClient.patch(`/live-lesson/${teachingUnitId}/quiz/settings`, payload);
  return response.data?.data || null;
}

export async function joinLiveLessonQuiz(joinToken: string): Promise<{
  sessionId: number;
  teachingUnitId: number;
  studentId: string;
  status: 'joined' | 'pending' | 'blocked';
}> {
  const response = await apiClient.post(`/student/quiz/${encodeURIComponent(joinToken)}/join`);
  return response.data?.data;
}

export async function fetchStudentLiveLessonQuizState(joinToken: string): Promise<StudentLiveLessonQuizState | null> {
  const response = await apiClient.get(`/student/quiz/${encodeURIComponent(joinToken)}/state`);
  return response.data?.data || null;
}

export async function submitStudentLiveLessonQuizAnswer(
  joinToken: string,
  payload: { selectedIndex: number }
): Promise<void> {
  await apiClient.post(`/student/quiz/${encodeURIComponent(joinToken)}/answer`, payload);
}

export async function fetchActiveStudentLiveLessonQuiz(): Promise<{
  sessionId: number;
  teachingUnitId: number;
  joinToken: string;
  topic: string;
  status: string;
  phase: string;
  studentId: string;
  studentDisplayName: string;
} | null> {
  const response = await apiClient.get('/student/quiz/active');
  return response.data?.data || null;
}
